import { useEffect, useState, useRef } from 'react';
import { User } from 'firebase/auth';
import {
  requestNotificationPermission,
  getFCMToken,
  saveFCMToken,
  setupForegroundMessageListener,
  getNotificationPermissionStatus,
  areNotificationsSupported,
  initializeMessaging
} from './fcm';
import { toast } from 'sonner';
import { useAppointmentConfirmationStore } from './appointmentConfirmationStore';

/**
 * Hook to initialize FCM for push notifications when user logs in
 * Handles:
 * - Requesting notification permission
 * - Getting and saving FCM token
 * - Setting up foreground message listener
 */
export function useFCMInitialization(user: User | null) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [permissionAsked, setPermissionAsked] = useState(false);
  const { openDialog } = useAppointmentConfirmationStore();
  const openDialogRef = useRef(openDialog);
  const listenerSetupRef = useRef(false);
  
  // Keep openDialog ref updated
  useEffect(() => {
    openDialogRef.current = openDialog;
  }, [openDialog]);

  useEffect(() => {
    // Prevent double initialization (React Strict Mode can cause this)
    if (listenerSetupRef.current) {
      console.log('[FCM Init] Listener already set up, skipping...');
      return;
    }
    
    // Only initialize if user is logged in and notifications are supported
    if (!user || !areNotificationsSupported()) {
      setIsInitialized(false);
      return;
    }

    // Check if we've already asked for permission in this session
    const hasAskedBefore = sessionStorage.getItem('fcm_permission_asked') === 'true';
    
    let unsubscribeListener: (() => void) | undefined;
    
    const initializeFCM = async () => {
      try {
        console.log('[FCM Init] Starting FCM initialization...');
        
        // Check current permission status
        const currentPermission = getNotificationPermissionStatus();
        console.log('[FCM Init] Current permission:', currentPermission);

        if (currentPermission === 'default' && !hasAskedBefore && !permissionAsked) {
          // Ask for permission if not yet asked
          console.log('[FCM Init] Requesting notification permission...');
          
          // Small delay to let the app load before showing permission prompt
          setTimeout(async () => {
            const granted = await requestNotificationPermission();
            setPermissionAsked(true);
            sessionStorage.setItem('fcm_permission_asked', 'true');
            
            if (granted) {
              console.log('[FCM Init] Permission granted');
              toast.success('Notifications Enabled', {
                description: 'You\'ll receive push notifications for important updates.'
              });
              
              // Get and save FCM token and setup listener
              unsubscribeListener = await setupToken();
            } else {
              console.log('[FCM Init] Permission denied');
              toast.info('Notifications Disabled', {
                description: 'You can enable notifications later in Settings.'
              });
            }
          }, 2000); // Wait 2 seconds after login
        } else if (currentPermission === 'granted') {
          // Permission already granted, just get/save token and setup listener
          console.log('[FCM Init] Permission already granted, setting up token and listener...');
          unsubscribeListener = await setupToken();
        }

        async function setupToken() {
          try {
            // Get FCM token
            const token = await getFCMToken();
            
            if (token && user?.uid) {
              console.log('[FCM Init] FCM token received, saving to Firestore...');
              await saveFCMToken(user.uid, token);
              console.log('[FCM Init] FCM token saved successfully');
              
              // Initialize messaging instance first
              const messaging = initializeMessaging();
              if (!messaging) {
                console.error('[FCM Init] Failed to initialize messaging');
                return;
              }
              
              // Setup foreground message listener
              console.log('[FCM Init] Setting up persistent foreground message listener...');
              const unsubscribe = setupForegroundMessageListener((payload) => {
                console.log('[FCM Init] 🔔 Foreground notification received:', payload);
                
                const { notification, data } = payload;
                
                // Handle appointment confirmation notification (special case)
                if (data?.type === 'appointment_confirmation_needed' && data?.appointmentId) {
                  console.log('[FCM Init] Opening appointment confirmation dialog');
                  openDialogRef.current(data.appointmentId, {
                    clientName: data.clientName,
                    serviceName: data.serviceName,
                    startTime: data.startTime,
                    endTime: data.endTime,
                    staffNames: data.staffNames,
                  });
                  
                  // Still show a toast notification
                  toast(notification?.title || 'Appointment Confirmation Needed', {
                    description: notification?.body || 'Please confirm your upcoming appointment',
                    duration: 5000,
                  });
                } else {
                  // Show toast notification for all other message types
                  const title = notification?.title || 'Notification';
                  const body = notification?.body || '';
                  
                  toast(title, {
                    description: body,
                    action: data?.url ? {
                      label: 'View',
                      onClick: () => window.location.href = data.url
                    } : undefined,
                    duration: 5000
                  });
                }
              });
              
              console.log('[FCM Init] ✅ Listener setup complete and will persist');
              listenerSetupRef.current = true;  // Mark as set up
              return unsubscribe;
            }
          } catch (error) {
            console.error('[FCM Init] Error setting up token:', error);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('[FCM Init] Error initializing FCM:', error);
      }
    };

    initializeFCM();

    // Cleanup - unsubscribe from listener when component unmounts
    // NOTE: This should ONLY run when user logs out, not on page navigation
    return () => {
      console.log('[FCM Init] Cleaning up FCM initialization');
      if (unsubscribeListener) {
        console.log('[FCM Init] Unsubscribing from message listener');
        unsubscribeListener();
        listenerSetupRef.current = false;
      }
    };
  }, [user]);  // REMOVED permissionAsked from dependencies to prevent re-runs

  return { isInitialized };
}
